import React, { useState } from 'react';
import './UserSettings.css';

function UserSettings({ onClose, username, onUpdate }) {
  const [activeTab, setActiveTab] = useState('password'); // password, username, delete
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      if (!currentPassword || !newPassword || !confirmPassword) {
        setError('All password fields are required');
        setIsLoading(false);
        return;
      }

      if (newPassword !== confirmPassword) {
        setError('New passwords do not match');
        setIsLoading(false);
        return;
      }

      if (newPassword.length < 6) {
        setError('New password must be at least 6 characters long');
        setIsLoading(false);
        return;
      }

      const response = await fetch('/api/users/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          currentPassword: currentPassword,
          newPassword: newPassword
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        setError(data.error || 'Failed to change password');
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again later.');
      console.error('Change password error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Change username
  const handleChangeUsername = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      if (!newUsername.trim()) {
        setError('New username is required');
        setIsLoading(false);
        return;
      }

      if (newUsername.trim().toLowerCase() === username.toLowerCase()) {
        setError('New username must be different from current username');
        setIsLoading(false);
        return;
      }

      const response = await fetch('/api/users/change-username', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentUsername: username,
          newUsername: newUsername.trim()
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Username changed successfully!');
        // Update localStorage
        localStorage.setItem('chat_username', newUsername.trim());
        setNewUsername('');
        setTimeout(() => {
          setSuccess(null);
          if (onUpdate) {
            onUpdate(newUsername.trim());
          }
        }, 2000);
      } else {
        setError(data.error || 'Failed to change username');
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again later.');
      console.error('Change username error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Double confirmation
    if (!deletePassword) {
      setError('Please enter your password to confirm account deletion');
      return;
    }

    const confirmText = window.prompt(
      'This action cannot be undone. Type "DELETE" to confirm:'
    );

    if (confirmText !== 'DELETE') {
      setError('Account deletion cancelled');
      setDeletePassword('');
      return;
    }

    setIsLoading(true);

    try {
      // First, get user info to find user ID
      const userResponse = await fetch(`/api/users/username/${encodeURIComponent(username)}`);
      if (!userResponse.ok) {
        throw new Error('Failed to fetch user information');
      }

      const userData = await userResponse.json();
      const userId = userData.user.id;

      // Verify password first
      const verifyResponse = await fetch('/api/users/verify-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          password: deletePassword
        }),
      });

      const verifyData = await verifyResponse.json();

      if (!verifyResponse.ok || !verifyData.valid) {
        setError('Incorrect password');
        setDeletePassword('');
        setIsLoading(false);
        return;
      }

      // Delete account
      const deleteResponse = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const deleteData = await deleteResponse.json();

      if (deleteResponse.ok) {
        // Clear all local storage and redirect to home
        sessionStorage.clear();
        localStorage.clear();
        alert('Your account has been deleted successfully.');
        window.location.href = '/';
      } else {
        setError(deleteData.error || 'Failed to delete account');
        setIsLoading(false);
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again later.');
      console.error('Delete account error:', err);
      setIsLoading(false);
    }
  };

  return (
    <div className="user-settings-overlay" onClick={onClose}>
      <div className="user-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-settings-header">
          <h2>⚙️ Settings</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="user-settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('password');
              setError(null);
              setSuccess(null);
            }}
          >
            🔑 Reset Password
          </button>
          <button
            className={`settings-tab ${activeTab === 'username' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('username');
              setError(null);
              setSuccess(null);
            }}
          >
            👤 Change Username
          </button>
          <button
            className={`settings-tab ${activeTab === 'delete' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('delete');
              setError(null);
              setSuccess(null);
            }}
          >
            🗑️ Delete Account
          </button>
        </div>

        <div className="user-settings-content">
          {error && (
            <div className="settings-error-message">❌ {error}</div>
          )}
          {success && (
            <div className="settings-success-message">✅ {success}</div>
          )}

          {/* Reset Password Tab */}
          {activeTab === 'password' && (
            <form onSubmit={handleResetPassword} className="settings-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password:</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  disabled={isLoading}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password:</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  disabled={isLoading}
                  required
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password:</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  disabled={isLoading}
                  required
                  minLength={6}
                />
              </div>
              <button type="submit" className="settings-submit-btn" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          )}

          {/* Change Username Tab */}
          {activeTab === 'username' && (
            <form onSubmit={handleChangeUsername} className="settings-form">
              <div className="form-group">
                <label htmlFor="currentUsername">Current Username:</label>
                <input
                  type="text"
                  id="currentUsername"
                  value={username}
                  disabled
                  className="disabled-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="newUsername">New Username:</label>
                <input
                  type="text"
                  id="newUsername"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Enter new username"
                  disabled={isLoading}
                  required
                />
              </div>
              <button type="submit" className="settings-submit-btn" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Change Username'}
              </button>
            </form>
          )}

          {/* Delete Account Tab */}
          {activeTab === 'delete' && (
            <form onSubmit={handleDeleteAccount} className="settings-form">
              <div className="delete-warning">
                <h3>⚠️ Warning: This action cannot be undone!</h3>
                <p>Deleting your account will:</p>
                <ul>
                  <li>Permanently delete all your account data</li>
                  <li>Remove your access to all features</li>
                  <li>Delete your associated access code</li>
                </ul>
                <p><strong>This cannot be reversed!</strong></p>
              </div>
              <div className="form-group">
                <label htmlFor="deletePassword">Enter Password to Confirm:</label>
                <input
                  type="password"
                  id="deletePassword"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  required
                />
              </div>
              <button 
                type="submit" 
                className="settings-delete-btn" 
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete My Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserSettings;
